import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Text } from '@/components/ui';
import { chartAxisTick, chartColor } from '@/lib/design-system/color-system';
import { cn } from '@/lib/design-system/cn';
import { fetchAllTransactions, fetchBudgets, fetchCategories } from '@/lib/finance/api';
import { formatUsd } from '@/lib/finance/format';
import {
  addMonths,
  monthEnd,
  monthLabel,
  monthLabelShort,
  monthStart,
  recentMonths,
} from '@/lib/finance/month';
import {
  OTHER_EXPENSE_CATEGORY_ID,
  UNCATEGORIZED_CATEGORY_ID,
  buildActualDrillModel,
  buildTrendMatrix,
  drillScopeForCategory,
  type BudgetBandStat,
  type DrillScope,
  type TrendMatrix,
  type TrendMetric,
} from '@/lib/finance/report';
import type { Budget, Category, Transaction } from '@/lib/finance/types';

import { ActualDrilldown, DrillNumber, drillKey } from './ActualDrilldown.web';

const WINDOW_MONTHS = 12;
/** Budgets are set ahead of time, so that view centres the current month: 2 back, 9 ahead. */
const BUDGET_MONTHS_BACK = 2;

/** `label` | `total` | month-column index. */
type SortKey = 'label' | 'total' | number;

const METRIC_TITLE: Record<TrendMetric, string> = {
  budget: 'Monthly budgets',
  actual: 'Monthly actuals',
  difference: 'Actual − Budget',
};

const BAND_OPTIONS: { key: BudgetBandStat; label: string }[] = [
  { key: 'min', label: 'Min' },
  { key: 'max', label: 'Max' },
  { key: 'average', label: 'Average' },
];

function fmtCell(value: number): string {
  return value === 0 ? '·' : formatUsd(value);
}

type OpenCell = { scope: DrillScope; month: string | null };

function openTitle(categories: Category[], scope: DrillScope, month: string | null): string {
  const when = month != null ? monthLabel(month) : 'these months';
  if (scope.kind === 'all-expenses') return `All expenses · ${when}`;
  if (scope.kind === 'uncategorized') return `Uncategorized · ${when}`;
  if (scope.kind === 'other') return `Other expenses · ${when}`;
  if (scope.kind === 'unbudgeted') return `Unbudgeted · ${when}`;
  const name = categories.find((category) => category.id === scope.categoryId)?.name ?? 'Category';
  return `${name} · ${when}`;
}

function openedCellUsd(matrix: TrendMatrix, months: string[], open: OpenCell): number {
  if (open.scope.kind === 'all-expenses') {
    if (open.month == null) return matrix.grandTotal;
    const index = months.indexOf(open.month);
    return index < 0 ? 0 : (matrix.totalsByMonth[index] ?? 0);
  }
  const id =
    open.scope.kind === 'category'
      ? open.scope.categoryId
      : open.scope.kind === 'uncategorized'
        ? UNCATEGORIZED_CATEGORY_ID
        : open.scope.kind === 'other'
          ? OTHER_EXPENSE_CATEGORY_ID
          : null;
  if (id == null) return 0;
  const row = matrix.rows.find((item) => item.categoryId === id);
  if (!row) return 0;
  if (open.month == null) return row.total;
  const index = months.indexOf(open.month);
  return index < 0 ? 0 : (row.values[index] ?? 0);
}

export function OverTimeReport({ month, metric }: { month: string; metric: TrendMetric }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [budgetsByMonth, setBudgetsByMonth] = useState<Map<string, Budget[]>>(new Map());
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [budgetStat, setBudgetStat] = useState<BudgetBandStat>('max');
  const [open, setOpen] = useState<OpenCell | null>(null);

  const months = useMemo(
    () =>
      metric === 'budget'
        ? Array.from({ length: WINDOW_MONTHS }, (_, i) => addMonths(month, i - BUDGET_MONTHS_BACK))
        : recentMonths(month, WINDOW_MONTHS),
    [month, metric],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const from = monthStart(months[0]!);
        const to = monthEnd(months[months.length - 1]!);
        const [categoryRows, transactionRows, ...budgetLists] = await Promise.all([
          fetchCategories(),
          fetchAllTransactions({ from, to }),
          ...months.map((m) => fetchBudgets(monthStart(m))),
        ]);
        if (cancelled) return;
        setCategories(categoryRows);
        setTransactions(transactionRows);
        const map = new Map<string, Budget[]>();
        months.forEach((m, i) => map.set(m, budgetLists[i] ?? []));
        setBudgetsByMonth(map);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to build report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [month, months]);

  const matrix = useMemo(
    () =>
      buildTrendMatrix(
        categories,
        budgetsByMonth,
        transactions,
        months,
        metric,
        metric === 'budget' ? budgetStat : 'max',
      ),
    [categories, budgetsByMonth, transactions, months, metric, budgetStat],
  );

  useEffect(() => {
    setOpen(null);
  }, [metric, month]);

  const drill = useMemo(() => {
    if (open == null || metric === 'budget') return null;
    const relevant = open.month != null ? [open.month] : months;
    const budgets: Budget[] = [];
    for (const key of relevant) budgets.push(...(budgetsByMonth.get(key) ?? []));
    return {
      key: drillKey(open.scope, open.month),
      title: openTitle(categories, open.scope, open.month),
      model: buildActualDrillModel({
        categories,
        transactions,
        budgets,
        scope: open.scope,
        month: open.month,
        windowMonths: months,
        mode: metric === 'difference' ? 'difference' : 'actual',
        cellUsd: openedCellUsd(matrix, months, open),
      }),
    };
  }, [open, metric, categories, transactions, budgetsByMonth, months, matrix]);

  const sortedRows = useMemo(() => {
    if (sortKey == null) return matrix.rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...matrix.rows].sort((a, b) => {
      if (sortKey === 'label') return a.label.localeCompare(b.label) * dir;
      const av = sortKey === 'total' ? a.total : (a.values[sortKey] ?? 0);
      const bv = sortKey === 'total' ? b.total : (b.values[sortKey] ?? 0);
      return (av - bv) * dir;
    });
  }, [matrix.rows, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'label' ? 'asc' : 'desc');
    }
  }

  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const chartData = useMemo(
    () => months.map((m, i) => ({ name: monthLabelShort(m), value: matrix.totalsByMonth[i] ?? 0 })),
    [months, matrix.totalsByMonth],
  );

  if (loading) return <ActivityIndicator />;
  if (error)
    return (
      <Text variant="body-sm" className="text-danger">
        {error}
      </Text>
    );

  const isDiff = metric === 'difference';
  const th = 'cursor-pointer select-none border-b border-border px-3 py-2 font-medium';
  const tdNum = 'border-b border-border px-3 py-1 text-right tabular-nums';
  const cellTone = (v: number) =>
    isDiff
      ? v > 0
        ? 'text-danger'
        : v < 0
          ? 'text-success'
          : 'text-fg-subtle'
      : v === 0
        ? 'text-fg-subtle'
        : 'text-fg';

  function selectCell(scope: DrillScope, cellMonth: string | null) {
    setOpen((current) =>
      current && drillKey(current.scope, current.month) === drillKey(scope, cellMonth)
        ? null
        : { scope, month: cellMonth },
    );
  }

  function valueCell(value: number, scope: DrillScope, cellMonth: string | null, what: string) {
    if (metric === 'budget') return fmtCell(value);
    return (
      <DrillNumber
        text={fmtCell(value)}
        selected={open != null && drillKey(open.scope, open.month) === drillKey(scope, cellMonth)}
        label={`Show transactions for ${what}`}
        onPress={() => selectCell(scope, cellMonth)}
      />
    );
  }

  return (
    <View>
      <View className="mb-2 flex-row flex-wrap items-center gap-3">
        <Text variant="h3">
          {METRIC_TITLE[metric]} ·{' '}
          {metric === 'budget'
            ? `${BUDGET_MONTHS_BACK} months back → ${WINDOW_MONTHS - BUDGET_MONTHS_BACK - 1} ahead`
            : `last ${WINDOW_MONTHS} months`}
        </Text>
        {metric === 'budget' ? (
          <View className="flex-row gap-1 rounded-lg bg-surface-raised p-1">
            {BAND_OPTIONS.map((item) => {
              const active = item.key === budgetStat;
              return (
                <Pressable
                  key={item.key}
                  className={cn('rounded-md px-3 py-1.5', active ? 'bg-surface' : '')}
                  onPress={() => setBudgetStat(item.key)}
                >
                  <Text
                    variant="body-sm"
                    className={active ? 'font-semibold text-accent' : 'text-muted'}
                  >
                    {item.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ) : null}
      </View>
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid stroke={chartColor.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={chartAxisTick} />
            <YAxis tick={chartAxisTick} />
            <Tooltip formatter={(value: unknown) => `$${formatUsd(Number(value))}`} />
            <Bar dataKey="value" fill={chartColor.series}>
              {isDiff
                ? chartData.map((d, i) => (
                    <Cell key={i} fill={d.value > 0 ? chartColor.expense : chartColor.income} />
                  ))
                : null}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="text-fg-muted">
              <th className={`${th} text-left`} onClick={() => toggleSort('label')}>
                Category{arrow('label')}
              </th>
              {months.map((m, i) => (
                <th
                  key={m}
                  className={`${th} text-right ${m === month ? 'text-accent' : ''}`}
                  onClick={() => toggleSort(i)}
                >
                  {monthLabelShort(m)}
                  {arrow(i)}
                </th>
              ))}
              <th className={`${th} text-right`} onClick={() => toggleSort('total')}>
                Total{arrow('total')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td className="px-3 py-4 text-fg-muted" colSpan={months.length + 2}>
                  No data for this period.
                </td>
              </tr>
            ) : (
              <>
                {sortedRows.map((row) => (
                  <tr key={row.categoryId}>
                    <td className="border-b border-border px-3 py-1 text-left font-medium">
                      {row.label}
                    </td>
                    {row.values.map((v, i) => (
                      <td key={months[i] ?? i} className={`${tdNum} ${cellTone(v)}`}>
                        {valueCell(
                          v,
                          drillScopeForCategory(row.categoryId),
                          months[i] ?? null,
                          `${row.label} ${monthLabelShort(months[i] ?? '')}`,
                        )}
                      </td>
                    ))}
                    <td className={`${tdNum} font-medium ${cellTone(row.total)}`}>
                      {valueCell(
                        row.total,
                        drillScopeForCategory(row.categoryId),
                        null,
                        `${row.label} total`,
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="border-t-2 border-border px-3 py-1 text-left">Total</td>
                  {matrix.totalsByMonth.map((t, i) => (
                    <td key={months[i] ?? i} className={`${tdNum} border-t-2`}>
                      {valueCell(
                        t,
                        { kind: 'all-expenses' },
                        months[i] ?? null,
                        monthLabelShort(months[i] ?? ''),
                      )}
                    </td>
                  ))}
                  <td className={`${tdNum} border-t-2`}>
                    {valueCell(matrix.grandTotal, { kind: 'all-expenses' }, null, 'all months')}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {drill && metric !== 'budget' ? (
        <ActualDrilldown
          key={drill.key}
          title={drill.title}
          model={drill.model}
          categories={categories}
          onClose={() => setOpen(null)}
        />
      ) : null}

      <Text variant="caption" className="mt-3 text-muted">
        Click a column header to sort.
        {metric === 'budget'
          ? ` Showing the ${budgetStat === 'average' ? 'midpoint' : budgetStat} of each category's min/max band.`
          : metric === 'actual'
            ? ' Actuals are expense charges in USD, the same total as ledger debit. Income, credits, and transfers are left out. Click a number to list its transactions.'
            : ' Each cell is expense charges minus the max budget. A budget on a category and its subcategory are added together. Click a number to list the transactions and budgets.'}
      </Text>
    </View>
  );
}
